import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Loader2, ShieldCheck } from "lucide-react";
import { useAdminAuth } from "./AdminAuth";
import { formatApiError } from "./api";

export default function AdminLogin() {
  const { login, verify2fa } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA challenge state
  const [challenge, setChallenge] = useState(null); // {txn_id, destination_email, destination_phone, device_name}
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) {
        setChallenge(res);
        setCode("");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verify2fa(challenge.txn_id, code, trust);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.requires_2fa) setChallenge(res);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f4f6fb] px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/saferide-logo.png" alt="SafeRide" className="h-10 w-auto" />
          <p className="mt-3 font-display text-xs font-semibold uppercase tracking-[0.22em] text-[#2340b5]">
            Admin Panel
          </p>
        </div>

        {!challenge ? (
          <form
            onSubmit={submit}
            className="rounded-2xl border border-[#12142b]/8 bg-white p-8 shadow-xl shadow-[#12142b]/5"
            data-testid="admin-login-form"
          >
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#12142b]">Sign in</h1>
            <p className="mt-1 text-sm text-[#565b73]">Restricted to super admins.</p>

            {error && (
              <div data-testid="admin-login-error" className="mt-5 rounded-lg bg-[#e4002b]/8 px-4 py-3 text-sm font-medium text-[#e4002b]">
                {error}
              </div>
            )}

            <label className="mt-6 block text-sm font-semibold text-[#12142b]">Email or phone</label>
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 focus-within:border-[#2340b5]">
              <Mail className="h-4 w-4 text-[#565b73]" strokeWidth={1.75} />
              <input
                data-testid="admin-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full bg-transparent py-3 text-sm text-[#12142b] outline-none placeholder:text-[#565b73]/60"
              />
            </div>

            <label className="mt-4 block text-sm font-semibold text-[#12142b]">Password</label>
            <div className="mt-2 flex items-center gap-2 rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] px-3 focus-within:border-[#2340b5]">
              <Lock className="h-4 w-4 text-[#565b73]" strokeWidth={1.75} />
              <input
                data-testid="admin-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full bg-transparent py-3 text-sm text-[#12142b] outline-none placeholder:text-[#565b73]/60"
              />
            </div>

            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
        ) : (
          <form
            onSubmit={submitCode}
            className="rounded-2xl border border-[#12142b]/8 bg-white p-8 shadow-xl shadow-[#12142b]/5"
            data-testid="twofa-form"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#2340b5]/8"><ShieldCheck className="h-5 w-5 text-[#2340b5]" /></span>
              <div>
                <h1 className="font-display text-2xl font-bold tracking-tight text-[#12142b]">Check your inbox</h1>
                <p className="mt-0.5 text-sm text-[#565b73]">New device: {challenge.device_name}</p>
              </div>
            </div>
            <p className="mt-4 text-sm text-[#565b73]" data-testid="twofa-destinations">
              We sent a 6-digit code to <span className="font-semibold text-[#12142b]">{challenge.destination_email}</span>
              {challenge.destination_phone && <> and <span className="font-semibold text-[#12142b]">{challenge.destination_phone}</span></>}.
              It expires in 10 minutes.
            </p>

            {error && (
              <div data-testid="twofa-error" className="mt-5 rounded-lg bg-[#e4002b]/8 px-4 py-3 text-sm font-medium text-[#e4002b]">
                {error}
              </div>
            )}

            <label className="mt-6 block text-sm font-semibold text-[#12142b]">Verification code</label>
            <input
              data-testid="twofa-code-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••"
              className="mt-2 w-full rounded-xl border border-[#12142b]/12 bg-[#f4f6fb] py-3 text-center font-display text-2xl font-bold tracking-[0.5em] text-[#12142b] outline-none focus:border-[#2340b5]"
            />

            <label className="mt-4 flex items-center gap-2.5 text-sm text-[#12142b]">
              <input
                data-testid="twofa-trust-checkbox"
                type="checkbox"
                checked={trust}
                onChange={(e) => setTrust(e.target.checked)}
                className="h-4 w-4 accent-[#2340b5]"
              />
              Trust this device — skip the code next time
            </label>

            <button
              data-testid="twofa-submit"
              type="submit"
              disabled={loading || code.length !== 6}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#2340b5] py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-[#1b3390] disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & sign in"}
            </button>

            <div className="mt-4 flex items-center justify-between text-sm">
              <button type="button" data-testid="twofa-back" onClick={() => { setChallenge(null); setError(""); }} className="font-semibold text-[#565b73] hover:text-[#12142b]">
                ← Back to sign in
              </button>
              <button type="button" data-testid="twofa-resend" onClick={resend} disabled={loading} className="font-semibold text-[#2340b5] hover:text-[#1b3390] disabled:opacity-60">
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
